// Modules:
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactGA from 'react-ga4';
import { useParams, Redirect, useLocation } from 'react-router-dom';

// Utils:
import { useErrorHandler } from 'react-error-boundary';
import { handleFetchError, formatColumns } from 'utilities/utils';
import {
    findThemeAndTab,
    setUrlsForEnvironment,
} from 'utilities/topicConfigUtils';
import TopicConfigContext from 'utilities/topicConfigContext';

// Config:
import config from 'config/config';

// Style:
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';

// Components:
import LinearProgress from '@mui/material/LinearProgress';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import NoData from 'components/UI/NoData';

import muiTheme from '../../theme';
import MapContent from './MapContent';
import ChartContent from './ChartContent';
import TextContent from './TextContent';
import TableContent from './TableContent';
import NCDMContent from './NCDMContent';
import CommunityProfile from './CommunityProfile';
import ButtonSwitch from './ButtonSwitch';
import About from './About';
import AttributeTable from './AttributeTable';
import TopicOverview from './TopicOverview';
import MissingSuppressed from '../UI/MissingSuppressed';
import ChartInfoModal from '../UI/ChartInfoModal';

const HealthTopic = ({
    topics,
    counties,
    ncdmContent,
    setNCDMContent,
    ncdmSelections,
    setNCDMSelections,
    setRouterParams,
}) => {
    // // Error Hook // //
    const handleError = useErrorHandler();

    // // State // //
    const [topicLoading, setTopicLoading] = useState(true);
    const [topicConfig, setTopicConfig] = useState(null);
    const [showDataUnavailable, setShowDataUnavailable] = useState({
        display: false,
        stratification: false,
    });
    // State object used in context provider - must match the outlined format below:
    const [contextData, setContextData] = useState({
        topic: null,
        theme: null,
        tab: null,
    });
    const [mapLayers, setMapLayers] = useState([]);
    const [layersLoading, setLayersLoading] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [topicNcdms, setTopicNcdms] = useState([]);
    const [mapDemographicContent, setMapDemographicContent] = useState(null);
    const [chartDemographicContent, setChartDemographicContent] = useState(
        null
    );
    const [mapZoom, setMapZoom] = useState(config.defaultMapZoom);
    const [
        missingSuppressedModalOpen,
        setMissingSuppressedModalOpen,
    ] = useState(false);
    const [chartInfoModalOpen, setChartInfoModalOpen] = useState(false);
    const [expand, setExpand] = useState(true);
    const [stratification, setStratification] = useState();
    const [currentData, setCurrentData] = useState();
    const [selection, setSelection] = useState();
    const [prevTab, setPrevTab] = useState();
    const [ageGroups, setAgeGroups] = useState([]);

    // // Refs // //
    const mapRef = useRef();

    // // Styles // //
    const styles = {
        '&.MuiAccordion-root': {
            overFlow: 'hidden',
        },
        '&.Mui-expanded': {
            minHeight: '3rem',
        },
        '&.MuiAccordionSummary-content': {
            color: 'blue',
        },
        '&.MuiAccordionDetails-root': {
            padding: 0,
        },
        '&.MuiTypography-root': {
            fontSize: '1rem',
            color: muiTheme.palette.text.secondary,
            marginRight: '0.625rem',
        },
        '&.MuiButtonBase-root': {
            width: 'fit-content',
            marginRight: '0.3rem',
        },
        '&.MuiButton-containedPrimary': {
            backgroundColor: muiTheme.palette.navbarBlue.main,
        },
    };

    // // Constants // //
    // In the useLocation pathname variable, index 1 is always the location of the topic:
    const PATHNAME_ARRAY_TOPIC_INDEX = 1;

    // // React Router variables // //
    const { theme, tab } = useParams();
    const { search, pathname } = useLocation();

    const topic = pathname.split('/')[PATHNAME_ARRAY_TOPIC_INDEX];

    let selectedLayerSet = null; // TODO: This is a placeholder for now to get it working, probably should be in state.

    // // Methods // //
    const handleChange = () => {
        setExpand(!expand);
    };

    const routeToLayers = (id, removing) => {
        let route = '';
        mapLayers
            .map((layer) => layer.id)
            .forEach((activeLayer) => {
                route += activeLayer;
            });
        // Add or remove id from string, depending on bool passed in
        return removing ? route.replace(id, '') : `${route + id}`;
    };

    const determineMainContent = () =>
        // eslint-disable-next-line no-nested-ternary
        contextData.tab.contentType === 'map' ? (
            <MapContent
                mapRef={mapRef}
                mapLayers={mapLayers}
                mapZoom={mapZoom}
                defaultMapZoom={config.defaultMapZoom}
                stateBounds={config.stateBounds}
                selectedLayerSet={selectedLayerSet}
                theme={theme}
                tab={tab}
                search={search}
                pathname={pathname}
                setMapDemographicContent={setMapDemographicContent}
                setChartDemographicContent={setChartDemographicContent}
                mapDemographicContent={mapDemographicContent}
                queryStringParse={queryStringParse}
                routeToLayers={routeToLayers}
                setMapLayers={setMapLayers}
                setMapZoom={setMapZoom}
                setTableData={setTableData}
                setLayersLoading={setLayersLoading}
                missingSuppressedModalOpen={missingSuppressedModalOpen}
                setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
                styles={styles}
            />
        ) : // eslint-disable-next-line no-nested-ternary
        contextData.tab.contentType === 'chart' ? (
            <ChartContent
                counties={counties}
                setChartDemographicContent={setChartDemographicContent}
                setTableData={setTableData}
                setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
                stratification={stratification}
                setStratification={setStratification}
                currentData={currentData}
                setCurrentData={setCurrentData}
                selection={selection}
                setSelection={setSelection}
                ageGroups={ageGroups}
                setAgeGroups={setAgeGroups}
                setChartInfoModalOpen={setChartInfoModalOpen}
                setShowDataUnavailable={setShowDataUnavailable}
                useAlternativeTitlesAndLabels={useAlternativeTitlesAndLabels} // TODO: Remove after full dynamic chart/tables title refactor
            />
        ) : // eslint-disable-next-line no-nested-ternary
        contextData.tab.contentType === 'text' ? (
            <TextContent setTableData={setTableData} />
        ) : // eslint-disable-next-line no-nested-ternary
        contextData.tab.contentType === 'table' ? (
            <TableContent
                setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
                stratification={stratification}
            />
        ) : // eslint-disable-next-line no-nested-ternary
        contextData.tab.contentType === 'ncdm' ? (
            <NCDMContent
                setNCDMContent={setNCDMContent}
                ncdmContent={ncdmContent}
                ncdmSelections={ncdmSelections}
                setNCDMSelections={setNCDMSelections}
                topicNcdms={topicNcdms}
                formatColumns={formatColumns}
                setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
            />
        ) : null;

    const determineSelectedLayerSet = () => {
        const { jurisdiction } = queryStringParse();
        const selectedGeometry = jurisdiction || 'primary'; // defaults to 'primary' when no jurisdiction is explicitly selected
        const layerSet = contextData.tab.mapSettings.mapSets.find(
            (layer) => layer.set === selectedGeometry
        );

        return layerSet;
    };

    const queryStringParse = useCallback(() => {
        // convert query string to key-value object
        const parameterString = search.substring(1); // removes leading '?'
        return parameterString
            .split('&')
            .map((pair) => pair.split('='))
            .reduce((agg, keyValueTuple) => {
                const [key, value] = keyValueTuple;
                agg[key] = value;
                return agg;
            }, {});
    }, [search]);

    const getLoader = () => {
        if (contextData.tab.contentType === 'map') {
            if (layersLoading.length) {
                return <LinearProgress />;
            }
            return (
                <LinearProgress
                    variant="determinate"
                    value={0}
                    valueBuffer={0}
                />
            );
        }
        return null;
    };

    // // Determine if the AttributeTable should be displayed
    const getAtttributeTable = (layerSet) => {
        switch (contextData.tab.contentType) {
            case 'table':
            case 'ncdm':
                return null;
            default:
                return (
                    <AttributeTable
                        counties={counties}
                        tableData={tableData}
                        isLoading={!tableData.length}
                        mapRef={mapRef}
                        formatColumns={formatColumns}
                        selectedLayerSet={layerSet}
                        setMissingSuppressedModalOpen={
                            setMissingSuppressedModalOpen
                        }
                        stratification={stratification}
                        // TODO: Remove after full dynamic chart/tables title refactor
                        useAlternativeTitlesAndLabels={
                            useAlternativeTitlesAndLabels
                        }
                    />
                );
        }
    };

    // // Effects // //
    useEffect(() => {
        setRouterParams({ topic, theme, tab });

        // Google Analytics update
        ReactGA.send({
            hitType: 'pageview',
            page: `${window.location.pathname + window.location.search}`,
        });

        // Clean up router params and fetched topic data when unmounting:
        return () => {
            setRouterParams({ topic: null, theme: null, tab: null });
        };
    }, [topic, theme, tab, search, setRouterParams, pathname]);

    // Effect to get the current topic configuration and use as context provider value:
    useEffect(() => {
        if (topic && !contextData.topic) {
            fetch(
                `${process.env.REACT_APP_EPHT_API}/config/topics/${topic}/FullConfig`
            )
                .then(handleFetchError)
                .then((res) => res.json())
                .then((json) => setUrlsForEnvironment(json))
                .then((json) => {
                    // If we are coming from a pasted URL or page refresh inside of a theme:
                    if (theme && tab) {
                        const foundThemeAndTab = findThemeAndTab(json, {
                            theme,
                            tab,
                        });

                        if (foundThemeAndTab) {
                            const [workingTheme, workingTab] = foundThemeAndTab;

                            setContextData({
                                topic: json,
                                theme: workingTheme,
                                tab: workingTab,
                            });
                        }
                    } else {
                        setContextData((prevContext) => ({
                            ...prevContext,
                            topic: json,
                        }));
                    }
                    setTopicConfig(json);
                })
                .catch(handleError);
        }
    }, [
        topic,
        contextData.topic,
        theme,
        tab,
        search,
        setRouterParams,
        pathname,
        handleError,
    ]);

    // Effect to manage loading state:
    useEffect(() => {
        if (topicConfig && contextData.theme && contextData.tab) {
            if (contextData.tab.showDataUnavailable === true) {
                setShowDataUnavailable({
                    ...showDataUnavailable,
                    display: true,
                });
            } else {
                setShowDataUnavailable({
                    ...showDataUnavailable,
                    display: false,
                });
            }
            setTopicLoading(false);
        }
    }, [topicConfig, contextData, contextData.theme, contextData.tab]);

    // Effect to set the current tab on tab or theme change within a topic:
    useEffect(() => {
        if (topicConfig && theme && tab) {
            const foundThemeAndTab = findThemeAndTab(topicConfig, {
                theme,
                tab,
            });

            if (foundThemeAndTab) {
                const [foundTheme, foundTab] = foundThemeAndTab;

                // Maintain topic, update theme and tab:
                setContextData((prevContext) => ({
                    ...prevContext,
                    theme: foundTheme,
                    tab: foundTab,
                }));
            }
        }
    }, [topicConfig, theme, tab]);

    // Fetch legend data for all map layers associated with current theme:
    useEffect(() => {
        const getLegendData = () => {
            if (!theme || !contextData.theme) {
                return;
            }
            // filter for tabs within the selected theme which have a map as their main content:
            const mapContentTabs = contextData.theme.tabs.filter(
                (x) => x.contentType === 'map'
            );
            if (!mapContentTabs.length) {
                return;
            }

            // a Set of all layer queries under this theme; avoids repetitive calls to duplicate endpoints
            const URLSet = new Set(
                mapContentTabs
                    .map((mapContentTab) =>
                        mapContentTab.mapSettings.mapSets
                            .map((layer) => layer.setLayers)
                            .flat()
                    )
                    .flat()
                    .map((layer) => layer.url)
            );

            // Convert Set to javascript Array:
            const metadataQueries = Array.from(URLSet);

            // Array of promises, each a call to map layer endpoints:
            const metadataPromises = metadataQueries.map((query) =>
                fetch(`${query}/?f=json`)
                    .then(handleFetchError)
                    .then((res) => res.json())
                    .then((json) => ({
                        url: query,
                        data: json.drawingInfo
                            ? json.drawingInfo.renderer
                            : null,
                    }))
                    // the following catch guarantees that the succeeding Promise.all will always resolve (catch returns a fulfilled promise)
                    .catch((error) => {
                        console.error('Error fetching,', error);
                        handleError(error);
                        return {
                            url: query,
                            data: {
                                error: true,
                            },
                        };
                    })
            );

            Promise.all(metadataPromises).then((drawingInfos) => {
                drawingInfos.forEach((info) =>
                    mapContentTabs
                        .map((mapContentTab) =>
                            mapContentTab.mapSettings.mapSets
                                .map((layer) => layer.setLayers)
                                .flat()
                        )
                        .flat()
                        .forEach((layer) => {
                            if (layer.url === info.url) {
                                layer.renderInfo = info.data; // updates each layer object with corresponding drawingInfo (cannot be named "drawingInfo" to avoid naming collision)
                            }
                        })
                );
            }, handleError);
        };
        getLegendData();
    }, [contextData.theme, theme, topic, topics, handleError]);

    // TODO: should consider moving this out of HealthTopics
    // Fetch NCDM config
    useEffect(() => {
        const omitNcdmData = contextData?.topic?.omitNcdmData;

        // Only fetching for topics that display Ncdm data
        if (omitNcdmData === false) {
            const getNcdmConfig = () => {
                const apiNcdmConfigEndpoint = `${config.ncdmConfigEndpoint}?topic=${contextData.topic.topicTitle}`;
                fetch(apiNcdmConfigEndpoint)
                    .then(handleFetchError)
                    .then((res) => res.json())
                    .then((json) => setTopicNcdms(json), handleError)
                    .catch(handleError);
            };
            getNcdmConfig();
        }
    }, [contextData.topic, setTopicNcdms, handleError]);

    // //  Render // //

    // This exists outside of the context provider so passing data directly:
    if (topicConfig) {
        // If no params passed in, go to health topic's general page
        if (theme === undefined) {
            return <TopicOverview topicConfig={topicConfig} />;
        }

        // Set our current location variables:
        const foundTheme = topicConfig.themes.find(
            ({ themePath }) => themePath === theme
        );

        const foundTab = foundTheme?.tabs.find(
            ({ tabPath }) => tabPath === tab
        );

        // If params exist, but are invalid, redirect to nearest root:
        if (theme && !foundTheme) {
            return <Redirect to={`/${topicConfig.topicUrlPath}`} />;
        }

        if (tab && !foundTab) {
            const { defaultTabPath } = foundTheme;
            return <Redirect to={`/${topic}/${theme}/${defaultTabPath}`} />;
        }

        // If all params valid:
        selectedLayerSet =
            contextData.tab?.contentType === 'map'
                ? determineSelectedLayerSet()
                : null;

        // TODO: The below logic relating to default stratification is only in use by the Asthma topic.
        // The idea is that when switching between themes in that topic, you move between two charts with differing default strats.
        // Should probably rework this approach when that topic is ready as the key defaultStratification is not present in the new
        // fetched config.

        // The state variable "stratification" is derived from props when the tab changes. The following will reset `stratification` when the tab changes.
        // `useEffect` cannot be used because useEffect will only trigger after the render is completed, at which point the wrong selection will be applied to children components resulting in errors for some charts.
        // For more, see: https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops

        // When a stratification is selected, it persists in state when navigating to a new tab or theme.  There are instances when a chart does not have stratified data for that particular stored stratification
        // which results in UI and attribute table oddities.  The following conditional is in place to reset the stratification to a 'defaultStratification' if this occurs.
        // if (
        //     contextData.tab.defaultStratification !== prevStrat ||
        //     contextData.tab.stratifications?.filter((strat) => {
        //         // In place to account for the 'All' stratification type, which is not stored within the topics.js object, it occurs when 'undefined' is passed to the state variable.
        //         if (stratification === undefined) {
        //             return 1;
        //         }
        //         return strat.field === stratification;
        //     }).length === 0
        // ) {
        //     // Handles charts with no stratifications and avoids an infinite re-render:
        //     if (currentTab.defaultStratification !== null) {
        //         setCurrentData(null);
        //         setStratification(currentTab.defaultStratification);
        //         setPrevStrat(currentTab.defaultStratification);
        //     }
        // }
    }

    const currentTab = contextData.tab;
    const currentChartSettings = currentTab?.chartSettings;

    if (currentTab && currentChartSettings) {
        // Handle tab change
        if (currentTab.tabPath !== prevTab?.tab) {
            // Reset stratification when switching tabs back to undefined which
            // sets stratification to "All":
            setStratification();
            setPrevTab((prev) => ({
                ...prev,
                tab: currentTab.tabPath,
            }));
        }

        // Handle selection reset on tab or theme change
        const newDefaultSelection = currentChartSettings.defaultSelection;
        if (
            newDefaultSelection !== prevTab?.defaultSelection ||
            theme !== prevTab?.theme
        ) {
            setCurrentData(null);
            setSelection(newDefaultSelection);
            setPrevTab((prev) => ({
                ...prev,
                defaultSelection: newDefaultSelection,
                theme,
            }));
        }
    }

    // TODO: This is in place as a temporary solution as we explore using MUI Skeleton
    if (topicLoading) {
        return (
            <div className="health-topic-loading-spinner-container">
                <CircularProgress />
            </div>
        );
    }

    // TODO: Temporary hard coded checks to determine whether we should use
    // the alternative table and chart titles/labels (swapping "Age-adjusted" for
    // "Age-specific") for specific TBI and Falls Disparities displays with age strat.
    const FALLS_ALTERNATIVE_LANGUAGE_TAB_PATHS = [
        'edTrend',
        'hospTrend',
        'mortalityTrend',
    ];

    const isTbiAgeStratifiedDisparities =
        contextData.topic.topicUrlPath === 'tbi' &&
        contextData.theme.themePath === 'disparities' &&
        stratification === 'age';

    const isFallsAgeStratifiedDisparities =
        contextData.topic.topicUrlPath === 'falls' &&
        contextData.theme.themePath === 'disparities' &&
        stratification === 'age' &&
        FALLS_ALTERNATIVE_LANGUAGE_TAB_PATHS.includes(contextData.tab.tabPath);

    const useAlternativeTitlesAndLabels =
        isTbiAgeStratifiedDisparities || isFallsAgeStratifiedDisparities;

    return (
        <TopicConfigContext.Provider value={contextData}>
            <Grid item container className="main-body">
                <Grid item xs={12} lg={8} className="main-content">
                    <Paper className="content-block">
                        <ButtonSwitch
                            theme={theme}
                            tab={tab}
                            ncdmContent={ncdmContent}
                            ncdmSelections={ncdmSelections}
                            topicNcdms={topicNcdms}
                        />
                        <div
                            className={`mainContentContainer ${
                                showDataUnavailable.display
                                    ? 'no-data-padding'
                                    : null
                            }`}
                        >
                            {showDataUnavailable.display ? (
                                <NoData
                                    stratification={
                                        showDataUnavailable.stratification
                                    }
                                />
                            ) : (
                                <>
                                    {!tableData.length &&
                                    contextData.tab.contentType !== 'ncdm' &&
                                    contextData.tab.contentType !== 'table' ? (
                                        <div className="blur-card"></div>
                                    ) : null}
                                    {getLoader()}
                                    {determineMainContent()}
                                </>
                            )}
                        </div>
                    </Paper>
                    {!showDataUnavailable.display &&
                        mapDemographicContent &&
                        contextData.tab.contentType !== 'chart' && (
                            <Paper className="content-block">
                                <CommunityProfile
                                    currentTopic={contextData.topic}
                                    mapDemographicContent={
                                        mapDemographicContent
                                    }
                                    setMapDemographicContent={
                                        setMapDemographicContent
                                    }
                                    chartDemographicContent={
                                        chartDemographicContent
                                    }
                                    setChartDemographicContent={
                                        setChartDemographicContent
                                    }
                                    setChartInfoModalOpen={
                                        setChartInfoModalOpen
                                    }
                                />
                            </Paper>
                        )}
                    {!showDataUnavailable.display &&
                        chartDemographicContent &&
                        contextData.tab.contentType === 'chart' &&
                        !contextData.tab.omitCommunityProfile && (
                            <>
                                <Accordion
                                    expanded={expand}
                                    onChange={handleChange}
                                >
                                    <div className="accordion-summary-container">
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="tableData-content"
                                            id="tableData-header-chart"
                                            sx={styles}
                                        >
                                            <Typography sx={styles}>
                                                {expand
                                                    ? 'Hide Demographic Info'
                                                    : 'Show Demographic Info'}
                                            </Typography>
                                        </AccordionSummary>
                                    </div>
                                    <AccordionDetails sx={styles}>
                                        <Paper>
                                            <CommunityProfile
                                                currentTopic={contextData.topic}
                                                currentTab={contextData.tab}
                                                mapDemographicContent={
                                                    mapDemographicContent
                                                }
                                                setMapDemographicContent={
                                                    setMapDemographicContent
                                                }
                                                chartDemographicContent={
                                                    chartDemographicContent
                                                }
                                                setChartDemographicContent={
                                                    setChartDemographicContent
                                                }
                                                setChartInfoModalOpen={
                                                    setChartInfoModalOpen
                                                }
                                            />
                                        </Paper>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}
                    <Paper className="content-block">
                        {!showDataUnavailable.display &&
                            !contextData.tab?.mapSettings?.timeSlider &&
                            getAtttributeTable(selectedLayerSet)}
                    </Paper>
                </Grid>

                <Grid item xs={12} lg={4} className="overview-content">
                    <About />
                </Grid>
                <MissingSuppressed
                    missingSuppressedModalOpen={missingSuppressedModalOpen}
                    setMissingSuppressedModalOpen={
                        setMissingSuppressedModalOpen
                    }
                />
                <ChartInfoModal
                    chartInfoModalOpen={chartInfoModalOpen}
                    setChartInfoModalOpen={setChartInfoModalOpen}
                />
            </Grid>
        </TopicConfigContext.Provider>
    );
};

export default HealthTopic;
